/**
 * Client-side error telemetry (R-telemetry).
 *
 * A tiny, dependency-light crash reporter: it keeps a rolling in-memory
 * BREADCRUMB trail (what the user did), hooks the browser's global error
 * channels, and on a real crash flushes a compact, scrubbed payload to the
 * server's `POST /telemetry/errors` route — which coalesces it into the issue
 * tracker. No SaaS, no bundle-size tax.
 *
 * FALSE-POSITIVE DISCIPLINE (the explicit design goal)
 * ----------------------------------------------------
 * The whole point of this is to surface REAL problems, so it is deliberately
 * conservative about what it REPORTS (sends to the server) vs. what it merely
 * records as a breadcrumb for context:
 *
 *   - `window.onerror` + `unhandledrejection` → REPORTED. These are genuine
 *     uncaught faults; that's the signal.
 *   - `console.error(...)` → REPORTED ONLY when its first argument is a real
 *     `Error` instance. A `console.error("some validation string")` the app
 *     logs on purpose is NOT a crash and is kept as a breadcrumb only.
 *   - Failed API responses (non-2xx) → BREADCRUMB ONLY, never a report. A 400
 *     validation or a 401-before-login is expected traffic, not a bug; it earns
 *     its place as context around a later real crash, not an issue of its own.
 *   - DEDUP: an identical fault signature within a short window is dropped, so
 *     a render-loop crash files once, not hundreds of times.
 *
 * Everything is injected (the dispatcher, the clock, the global target) so the
 * installer is unit-testable with no real `window`/`navigator`.
 */

import {
  TELEMETRY_LIMITS,
  type TelemetryBreadcrumb,
  type TelemetryBreadcrumbKind,
  type TelemetryErrorKind,
  type TelemetryErrorWireRequest,
} from "@rufus/shared-types";

/** Window of time (ms) within which an identical fault signature is a duplicate. */
export const DEDUP_WINDOW_MS = 5_000;

/** A build stamp for the SPA bundle (git sha + build time), from runtime config. */
export interface ClientBuildStamp {
  readonly gitSha?: string;
  readonly builtAt?: string;
}

/** The current campaign/turn a crash should be anchored to, if any. */
export interface TelemetryAnchor {
  campaignId?: string;
  turnId?: string;
}

/**
 * Module-level "where is the user right now" holder for crash anchoring.
 *
 * Telemetry is installed once at startup (in `main.tsx`), but the active
 * campaign/turn only exists later, inside the play view, and changes as the user
 * navigates. Rather than thread a telemetry handle through the component tree,
 * the play view publishes its current anchor here via {@link setTelemetryAnchor}
 * and the installed reporter reads it live via {@link getTelemetryAnchor} at
 * flush time. A deliberately tiny mutable module global — the one piece of
 * cross-cutting "ambient context" the crash reporter needs.
 */
let currentAnchor: TelemetryAnchor = {};

/** Publish the current crash anchor (call on campaign enter / turn change). */
export function setTelemetryAnchor(anchor: TelemetryAnchor): void {
  currentAnchor = { ...anchor };
}

/** Clear the crash anchor (call on leaving the play view). */
export function clearTelemetryAnchor(): void {
  currentAnchor = {};
}

/** Read the current crash anchor (used as the default `getAnchor`). */
export function getTelemetryAnchor(): TelemetryAnchor {
  return currentAnchor;
}

/** How a captured error is dispatched to the server (injected for tests). */
export type TelemetryDispatcher = (
  endpoint: string,
  payload: TelemetryErrorWireRequest,
) => void;

/** Options for {@link installTelemetry}. */
export interface TelemetryOptions {
  /** REST API base URL (config.apiUrl) — the telemetry route hangs off it. */
  readonly apiBaseUrl: string;
  /**
   * Supplies the current Cognito id token (bearer) at flush time, or null when
   * signed out. REQUIRED (not optional): the telemetry route is authenticated,
   * so a report with no token is dropped by the server — making this optional
   * was a footgun that let a caller silently install a pipeline that records
   * breadcrumbs, dedups, builds payloads, and sends NOTHING. A function (not a
   * value) so it reads the freshest token each flush; it may still RETURN null
   * when signed out (the dispatcher then skips the send).
   */
  readonly getToken: () => string | null;
  /** The SPA build stamp (config.buildInfo), recorded on each report. */
  readonly clientBuild?: ClientBuildStamp;
  /**
   * Supplies the CURRENT campaign/turn anchor at flush time, if the app is in a
   * campaign — so a crash is attributable to a transcript position. A function
   * (not a value) so it reads live state each flush. Returns `{}` when outside a
   * campaign.
   */
  readonly getAnchor?: () => { campaignId?: string; turnId?: string };
  /** Injected dispatcher. Defaults to a keepalive-fetch sender with the bearer. */
  readonly dispatch?: TelemetryDispatcher;
  /** Injected clock (ms). Defaults to `Date.now`. */
  readonly now?: () => number;
  /**
   * The global event target to attach handlers to. Defaults to `window`.
   * Injected so a test can pass a fake EventTarget.
   */
  readonly target?: TelemetryTarget;
  /** Supplies the current page URL at flush time. Defaults to `location.href`. */
  readonly getUrl?: () => string | undefined;
}

/** The slice of `window` the installer touches (keeps it mockable). */
export interface TelemetryTarget {
  addEventListener(type: string, listener: (ev: unknown) => void): void;
  onerror?: unknown;
}

/** The live controller returned by {@link installTelemetry}. */
export interface TelemetryController {
  /** Record a breadcrumb (coarse, secret-free). */
  addBreadcrumb(kind: TelemetryBreadcrumbKind, message: string): void;
  /** Manually report an error (used by the console.error hook + callers). */
  report(kind: TelemetryErrorKind, message: string, stack?: string): void;
  /** A snapshot of the current breadcrumb trail (for tests/inspection). */
  readonly breadcrumbs: readonly TelemetryBreadcrumb[];
}

function clamp(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** Reduce a page URL to path only (drop origin/query/fragment) for a breadcrumb. */
function pathOnly(url: string | undefined): string {
  if (!url) return "";
  return url.replace(/^[a-z]+:\/\/[^/]+/i, "").replace(/[?#].*$/, "");
}

/**
 * The default dispatcher: a `keepalive` `fetch` carrying the Cognito bearer.
 *
 * The telemetry route is AUTHENTICATED, so the request MUST carry the
 * `Authorization` header — which rules out `navigator.sendBeacon` (it cannot set
 * headers). `keepalive: true` recovers the property that mattered about the
 * beacon: the request still completes even if the page is unloading/navigating
 * right after a crash. Fire-and-forget; any failure is swallowed (a lost error
 * report must never itself raise). When there is no token (signed out) the
 * dispatch is skipped — the server would reject it anyway.
 *
 * (#18 note: the `Authorization` header makes this a CORS-preflighted request
 * regardless of content-type, so there is no `text/plain` optimization to make
 * — the preflight is unavoidable once the route requires auth. The REST API's
 * `defaultCorsPreflightOptions` already answers the OPTIONS preflight.)
 */
export function defaultDispatcher(getToken?: () => string | null): TelemetryDispatcher {
  return (endpoint, payload) => {
    try {
      const token = getToken?.() ?? null;
      // No token ⇒ the authenticated route would 401; don't bother sending.
      if (!token) return;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      void globalThis.fetch?.(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Never let telemetry dispatch throw into the app.
    }
  };
}

/** Server-side STACK_SIGNATURE_FRAMES — the client mirrors it so its dedup is
 * never COARSER than server grouping (round-12 Low #9). */
const DEDUP_STACK_FRAMES = 3;

/**
 * Build the client-side dedup key (cheap + stable-enough). Deliberately:
 *   - EXCLUDES the error `kind`, so a single fault surfaced through two channels
 *     (React 18 re-throws a render error to `window.onerror` AND logs it via
 *     `console.error`) collapses to ONE key instead of two (#15).
 *   - Keys on the TOP {@link DEDUP_STACK_FRAMES} STACK FRAMES (`at …`), not the
 *     `ErrorName: message` header, MIRRORING the server's structural signature
 *     (round-12 Low #9). Using only the FIRST frame could suppress a report the
 *     server would have grouped SEPARATELY — two faults that share a top frame
 *     but diverge deeper — so the second fault would never be sent. Three frames
 *     keeps client suppression no coarser than server grouping. Falls back to
 *     the collapsed message when no frame is present.
 */
/** Matches a V8 (`at …`) OR a Firefox/Safari (`fn@url:line:col`) frame line.
 * One shared predicate so the accepted grammars + the server's can't drift
 * (round-13 Low #7: a V8-only filter dropped FF/Safari frames, degrading the key
 * to the message and making suppression coarser than server grouping there). */
function isFrameLine(l: string): boolean {
  return /^at\s/i.test(l) || /^[^\s:@]*@\S+:\d/.test(l);
}

function dedupKey(message: string, stack?: string): string {
  const lines = (stack ?? "").split("\n").map((l) => l.trim()).filter((l) => l.length > 0);
  const frameLines = lines.filter(isFrameLine).slice(0, DEDUP_STACK_FRAMES);
  // Strip volatile line:col so the same fault across renders/builds dedups.
  const frames = frameLines.map((l) => l.replace(/:\d+:\d+/g, "")).join("|").trim();
  // COLLAPSE volatile numbers in the message (round-5 Medium): the SERVER's
  // structural signature ignores the message entirely for a stacked fault and
  // collapses digits/hex/uuids in its message shape-hash fallback. If the client
  // dedup kept raw digits, a crash loop whose message embeds a changing index
  // would produce a fresh key every time, evade the 5s window, and flood the
  // (shared) telemetry route even though every report coalesces onto one issue.
  // Collapsing the same volatile shapes aligns the client's "same fault" with the
  // server's so the dedup actually suppresses the loop. (A coarse collapse is
  // enough — it only has to match repeats, not be a cryptographic key.) When a
  // stack is present the FRAMES dominate the key; the message is a tiebreaker.
  const collapsedMessage = message
    .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, "<uuid>")
    .replace(/\b[0-9a-f]{8,}\b/gi, "<hex>")
    .replace(/\d+/g, "<n>");
  return `${collapsedMessage}|${frames}`;
}

/**
 * Absolute per-session cap on telemetry reports actually SENT (round-5 Medium).
 * The dedup window suppresses repeats of the SAME fault, but a crash loop whose
 * fault genuinely varies (or that mutates faster than dedup can collapse) could
 * otherwise send without bound and burn the stage-wide request quota that
 * gameplay routes share. A hard session ceiling bounds the blast radius of ANY
 * client to a fixed number of reports per page load — enough to capture the
 * pattern, not enough to flood. Sized well above a plausible real crash count.
 */
export const MAX_REPORTS_PER_SESSION = 50;

/**
 * Install the client telemetry pipeline. Returns a controller for breadcrumbs +
 * manual reporting. Idempotent-safe to call once at startup. NEVER throws into
 * the app — a failure to install telemetry must not break the SPA.
 */
export function installTelemetry(options: TelemetryOptions): TelemetryController {
  const now = options.now ?? (() => Date.now());
  const dispatch = options.dispatch ?? defaultDispatcher(options.getToken);
  const target = options.target ?? (globalThis.window as unknown as TelemetryTarget | undefined);
  const getUrl =
    options.getUrl ?? (() => globalThis.location?.href as string | undefined);
  const endpoint = `${options.apiBaseUrl.replace(/\/+$/, "")}/telemetry/errors`;

  // Ring buffer of breadcrumbs, capped to the shared limit.
  const buffer: TelemetryBreadcrumb[] = [];
  function addBreadcrumb(kind: TelemetryBreadcrumbKind, message: string): void {
    buffer.push({ at: now(), kind, message: clamp(message, TELEMETRY_LIMITS.maxBreadcrumbLen) });
    if (buffer.length > TELEMETRY_LIMITS.maxBreadcrumbs) buffer.shift();
  }

  // Dedup: last-seen timestamp per signature. Pruned opportunistically so it
  // can't grow unbounded over a long session.
  const lastSeen = new Map<string, number>();
  function isDuplicate(key: string): boolean {
    const t = now();
    const prev = lastSeen.get(key);
    const duplicate = prev !== undefined && t - prev < DEDUP_WINDOW_MS;
    // (#8) Only refresh lastSeen when we are NOT suppressing — i.e. when this
    // report will actually be SENT. If we stamped `t` on every call (including
    // suppressed duplicates), a fault recurring faster than DEDUP_WINDOW_MS would
    // keep SLIDING the window forward and NEVER re-report: a render loop firing
    // every ~1s would file exactly once and then stay silent forever, even hours
    // later. Anchoring the window to the last SENT report instead means a
    // sustained crash re-files once per window — enough to show it's ongoing
    // without flooding — and the opportunistic prune still bounds the map.
    if (!duplicate) lastSeen.set(key, t);
    if (lastSeen.size > 200) {
      for (const [k, ts] of lastSeen) if (t - ts > DEDUP_WINDOW_MS) lastSeen.delete(k);
    }
    return duplicate;
  }

  // Absolute per-session sent-report counter (round-5 Medium). Bounds how much
  // ANY client can send over the life of this page load, independent of dedup.
  let reportsSent = 0;

  function report(kind: TelemetryErrorKind, message: string, stack?: string): void {
    try {
      const msg = message.trim();
      if (msg.length === 0) return;
      // TOKEN GATE FIRST (round-6 Low): the route is authenticated, so a report
      // with no bearer would be dropped by the dispatcher/authorizer anyway.
      // Check it BEFORE touching dedup/session-cap state so a pre-auth
      // (boot/login) error that can't be sent does NOT consume the dedup window
      // (which would suppress the same fault for 5s after login) nor the
      // per-session send budget (which a noisy pre-auth page could exhaust,
      // disabling telemetry for the whole document after login). A no-token
      // report is a clean no-op; the next post-login occurrence reports normally.
      // FALSY check, not `=== null` (round-8 Low): an empty-string token (`""`
      // from a token-exchange response with an empty `id_token`) also can't be
      // sent — the dispatcher drops it on its own falsy check — so it must NOT
      // reach the dedup/cap state either, or it would consume the window + a slot
      // for a report that is never dispatched. `if (!token)` covers null, "", and
      // undefined alike.
      if (!options.getToken()) return;
      // Hard session ceiling checked BEFORE dedup (round-7 Low): past the cap we
      // drop as a TRUE no-op — if dedup ran first it would keep normalizing and
      // stamping the lastSeen map for reports that can never be sent. A crash
      // loop whose fault genuinely varies would otherwise evade dedup and flood
      // the shared telemetry route; the cap bounds the blast radius while still
      // capturing a generous sample of the session's faults.
      if (reportsSent >= MAX_REPORTS_PER_SESSION) return;
      if (isDuplicate(dedupKey(msg, stack))) return;

      const anchor = (options.getAnchor ?? getTelemetryAnchor)();
      const payload: TelemetryErrorWireRequest = {
        kind,
        message: clamp(msg, TELEMETRY_LIMITS.maxMessageLen),
        ...(stack ? { stack: clamp(stack, TELEMETRY_LIMITS.maxStackLen) } : {}),
        ...(getUrl() ? { url: pathOnly(getUrl()) } : {}),
        breadcrumbs: buffer.slice(),
        ...(options.clientBuild ? { clientBuild: options.clientBuild } : {}),
        ...(anchor.campaignId ? { campaignId: anchor.campaignId } : {}),
        ...(anchor.turnId ? { turnId: anchor.turnId } : {}),
      };
      reportsSent += 1; // count only reports that pass dedup + the session cap
      dispatch(endpoint, payload);
    } catch {
      // Reporting must never raise into the app.
    }
  }

  // --- Global error channels ------------------------------------------------
  // Guard the whole install so a missing/odd global target can't break startup.
  try {
    if (target) {
      // Synchronous uncaught exceptions.
      target.addEventListener("error", (ev: unknown) => {
        const e = ev as { message?: unknown; error?: { message?: unknown; stack?: unknown } };
        const stack = typeof e?.error?.stack === "string" ? e.error.stack : undefined;
        // Prefer the real `Error` object's message over `ErrorEvent.message`
        // (round-15 Medium #1): Chrome prefixes the latter with `Uncaught ` /
        // `Uncaught (in promise) `, which no longer matches the stack HEADER
        // (`Name: msg`). Taking `event.error.message` keeps the message and its
        // stack header aligned so the server's provenance strip can attribute
        // the message prefix instead of parsing a continuation line as topology.
        // Fall back to `ErrorEvent.message` for the stackless/cross-origin case.
        const errMsg = typeof e?.error?.message === "string" ? e.error.message : undefined;
        const message =
          errMsg !== undefined && errMsg.length > 0
            ? errMsg
            : typeof e?.message === "string"
              ? e.message
              : "Uncaught error";
        report("uncaught_exception", message, stack);
      });

      // Unhandled promise rejections.
      target.addEventListener("unhandledrejection", (ev: unknown) => {
        const reason = (ev as { reason?: unknown })?.reason;
        const { message, stack } = describeReason(reason);
        report("unhandled_rejection", message, stack);
      });
    }
  } catch {
    // Telemetry install is best-effort; a failure here leaves the app untouched.
  }

  const controller: TelemetryController = {
    addBreadcrumb,
    report,
    get breadcrumbs() {
      return buffer.slice();
    },
  };
  activeController = controller;
  return controller;
}

/**
 * The controller from the most recent {@link installTelemetry}, so cross-cutting
 * producers (e.g. route changes) can drop a breadcrumb without a handle threaded
 * through the component tree — the same ambient-context pattern as the anchor
 * holder. `undefined` before install (a breadcrumb then is a safe no-op).
 */
let activeController: TelemetryController | undefined;

/**
 * Record a NAVIGATION breadcrumb (a path-only route change) on the active
 * telemetry controller — the "where did the user go" half of the trail that the
 * click/API producers don't capture. Path-only (no query/fragment, which can
 * carry tokens), so it is secret-free. A no-op if telemetry isn't installed.
 * Called from the app's view transitions (e.g. entering a campaign).
 */
export function recordNavigationBreadcrumb(path: string): void {
  const clean = path.replace(/[?#].*$/, "");
  activeController?.addBreadcrumb("navigation", clean);
}

/**
 * Describe a promise rejection reason into a message + optional stack.
 *
 * SECRET-SAFE (#6): a real `Error` yields its (developer-authored) message +
 * stack; a string reason is taken verbatim; but ANY OTHER value (a rejected
 * `Response`, a plain object carrying a response body, form state, …) is
 * described by its CONSTRUCTOR NAME ONLY — never `JSON.stringify`d. Serializing
 * an arbitrary rejection reason is exactly how a token or PII would ride into a
 * stored issue; we refuse to, and emit a coarse shape descriptor instead.
 */
export function describeReason(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) {
    return { message: reason.message || reason.name, ...(reason.stack ? { stack: reason.stack } : {}) };
  }
  if (typeof reason === "string") return { message: reason };
  return { message: `Non-Error rejection: ${describeShape(reason)}` };
}

/**
 * A COARSE, value-free descriptor of an arbitrary value for a breadcrumb/message
 * (#6): a primitive's type, or an object's constructor name plus its top-level
 * KEY NAMES — never the values. So `{ password: "x", user: {...} }` becomes
 * `Object{password,user}`, carrying the shape a developer needs to recognize it
 * without leaking a single stored value.
 */
export function describeShape(value: unknown): string {
  if (value === null) return "null";
  const t = typeof value;
  if (t !== "object") return t; // number | boolean | undefined | bigint | symbol | function
  const ctor = (value as { constructor?: { name?: string } }).constructor?.name ?? "Object";
  if (Array.isArray(value)) return `${ctor}[${value.length}]`;
  let keys: string[] = [];
  try {
    keys = Object.keys(value as object).slice(0, 20);
  } catch {
    keys = [];
  }
  return `${ctor}{${keys.join(",")}}`;
}

/**
 * Wrap `console.error` so a call carrying a real `Error` in ANY argument
 * position is REPORTED (`console_error`), but a plain `console.error("a string",
 * …)` with no Error is NOT — it becomes a context breadcrumb only
 * (false-positive discipline). Returns a restore function; the original
 * `console.error` is always still called, so app logging is unchanged. Separated
 * from {@link installTelemetry} so a caller can opt in/out independently.
 *
 * SECRET-SAFE breadcrumb (#6): the non-Error path records string arguments
 * verbatim but describes any object/other argument by its SHAPE
 * ({@link describeShape} — constructor + key names), never its serialized
 * values, so a `console.error("save failed", formState)` cannot ship the form's
 * contents.
 */
export function captureConsoleErrors(
  controller: Pick<TelemetryController, "report" | "addBreadcrumb">,
  consoleObj: { error: (...args: unknown[]) => void } = console,
): () => void {
  const original = consoleObj.error.bind(consoleObj);
  consoleObj.error = (...args: unknown[]) => {
    try {
      const firstError = args.find((a): a is Error => a instanceof Error);
      if (firstError) {
        // A real Error logged to the console — report it.
        controller.report(
          "console_error",
          firstError.message || firstError.name,
          firstError.stack,
        );
      } else {
        // A plain console.error(...) — context only, never a report.
        const text = args.map(stringifyArg).join(" ");
        if (text.trim().length > 0) controller.addBreadcrumb("console", clampLabel(text));
      }
    } catch {
      // Capture must never break logging.
    }
    original(...args);
  };
  return () => {
    consoleObj.error = original;
  };
}

/**
 * The minimal breadcrumb-recording surface a producer (the click listener, the
 * API client) needs — a strict subset of {@link TelemetryController}. Taking the
 * narrow sink (not the whole controller) keeps producers decoupled from the
 * reporter and trivially mockable.
 */
export type BreadcrumbSink = Pick<TelemetryController, "addBreadcrumb">;

/**
 * Describe a clicked element as a COARSE, secret-free selector for a breadcrumb:
 * `tag#id.firstClass`, walking up to the nearest actionable ancestor (button /
 * link / `[role]`) so a click on an icon inside a button is attributed to the
 * button. NEVER includes text content or input values — only the structural
 * descriptor. Exported for direct testing.
 */
export function describeClickTarget(start: unknown): string {
  // Walk up to a meaningful, actionable element.
  let el = start as
    | { tagName?: string; id?: string; className?: unknown; getAttribute?: (n: string) => string | null; parentElement?: unknown }
    | null
    | undefined;
  for (let hops = 0; el && hops < 5; hops += 1) {
    const tag = typeof el.tagName === "string" ? el.tagName.toLowerCase() : "";
    const isActionable =
      tag === "button" ||
      tag === "a" ||
      (typeof el.getAttribute === "function" && el.getAttribute("role") !== null);
    if (isActionable || hops === 0) {
      if (isActionable || !el.parentElement) return formatElement(el);
    }
    el = el.parentElement as typeof el;
  }
  return "(unknown)";
}

function formatElement(el: {
  tagName?: string;
  id?: string;
  className?: unknown;
}): string {
  const tag = typeof el.tagName === "string" ? el.tagName.toLowerCase() : "element";
  const id = typeof el.id === "string" && el.id.length > 0 ? `#${el.id}` : "";
  // Only the FIRST class, structural not stateful — enough to disambiguate,
  // never a full (possibly dynamic/utility-spammed) class list.
  const className =
    typeof el.className === "string" && el.className.trim().length > 0
      ? `.${el.className.trim().split(/\s+/)[0]}`
      : "";
  return `${tag}${id}${className}`;
}

/**
 * Install a capturing-phase click listener that records a coarse `click`
 * breadcrumb for each click — the "what did the user press" half of the trail.
 * Capturing phase so it still fires even if a handler stops propagation. Coarse
 * selector only (no text/values), so it is secret-free. Best-effort: a listener
 * failure never surfaces. Returns a remove function.
 */
export function installClickBreadcrumbs(
  sink: BreadcrumbSink,
  target: Pick<TelemetryTarget, "addEventListener"> | undefined = globalThis.window as
    | TelemetryTarget
    | undefined,
): () => void {
  if (!target) return () => {};
  const listener = (ev: unknown) => {
    try {
      const t = (ev as { target?: unknown })?.target;
      if (t) sink.addBreadcrumb("click", describeClickTarget(t));
    } catch {
      // Never let breadcrumb capture break a click.
    }
  };
  try {
    // Capturing phase (3rd arg true) — fire before any stopPropagation.
    (target.addEventListener as (t: string, l: (ev: unknown) => void, opts?: unknown) => void)(
      "click",
      listener,
      true,
    );
  } catch {
    return () => {};
  }
  return () => {
    const rm = (target as { removeEventListener?: (t: string, l: (ev: unknown) => void, opts?: unknown) => void })
      .removeEventListener;
    if (typeof rm === "function") rm.call(target, "click", listener, true);
  };
}

function stringifyArg(a: unknown): string {
  if (typeof a === "string") return a;
  if (a instanceof Error) return a.message;
  // Never JSON.stringify an arbitrary value into a stored breadcrumb (#6) — a
  // shape descriptor (constructor + key names) only, never the values.
  return describeShape(a);
}

function clampLabel(s: string): string {
  return s.length > TELEMETRY_LIMITS.maxBreadcrumbLen
    ? `${s.slice(0, TELEMETRY_LIMITS.maxBreadcrumbLen)}…`
    : s;
}
